export type AdminPermissionKey =
  | "dashboard"
  | "users"
  | "products"
  | "categories"
  | "orders"
  | "coupons"
  | "banners"
  | "reviews"
  | "notifications"
  | "support"
  | "settings"
  | "admins"
  | "profile";

type AdminPermissionMeta = {
  key: AdminPermissionKey;
  label: string;
  description?: string;
};

export type AdminPermissionState = {
  name: AdminPermissionKey;
  isActive: boolean;
};

export const ASSIGNABLE_ROLES: Array<"manager" | "support"> = ["manager", "support"];

export const ADMIN_PERMISSIONS: AdminPermissionMeta[] = [
  { key: "dashboard", label: "Dashboard", description: "View dashboard analytics" },
  { key: "users", label: "Users", description: "View and manage customer accounts" },
  { key: "products", label: "Products", description: "View and manage products" },
  { key: "categories", label: "Categories", description: "View and manage categories" },
  { key: "orders", label: "Orders", description: "View and manage orders" },
  { key: "coupons", label: "Coupons", description: "View and manage coupons" },
  { key: "banners", label: "Banners", description: "View and manage banners" },
  { key: "reviews", label: "Reviews", description: "View and manage reviews" },
  { key: "notifications", label: "Notifications", description: "Send push and in-app notifications" },
  { key: "support", label: "Support", description: "Manage customer support tickets" },
  { key: "settings", label: "Settings", description: "Manage app settings" },
  { key: "admins", label: "Admin Management", description: "Create admins and assign permissions" },
  { key: "profile", label: "Profile", description: "Update own profile and password" },
];

export const ASSIGNABLE_PERMISSIONS = ADMIN_PERMISSIONS.filter(
  (item) => item.key !== "profile",
);

const ROUTE_PERMISSIONS: Record<string, AdminPermissionKey> = {
  "/dashboard": "dashboard",
  "/users": "users",
  "/sale-users": "users",
  "/users/tree": "users",
  "/add-users": "users",
  "/add-sale-users": "users",
  "/withdrawals": "users",
  "/income-history": "users",
  "/profit-share": "users",
  "/products": "products",
  "/add-products": "products",
  "/categories": "categories",
  "/add-categories": "categories",
  "/orders": "orders",
  "/coupons": "coupons",
  "/banners": "banners",
  "/reviews": "reviews",
  "/order-feedback": "reviews",
  "/notifications": "notifications",
  "/support": "support",
  "/settings": "settings",
  "/club-income": "settings",
  "/admins": "admins",
  "/add-admins": "admins",
  "/profile": "profile",
};

export const getAdminProfile = () => {
  try {
    const stored = localStorage.getItem("adminProfile");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const getAdminRole = (): string => getAdminProfile()?.role || "";

export const isSuperAdmin = (): boolean => getAdminRole() === "superadmin";

const isPermissionEnabled = (item: any): boolean => {
  if (typeof item === "string") return true;
  return item?.isActive === true || item?.status === true;
};

export const hasPermission = (permission: AdminPermissionKey): boolean => {
  const profile = getAdminProfile();
  if (!profile) return false;
  if (profile.role === "superadmin") return true;
  if (permission === "profile") return true;

  return (profile.permissions || []).some((item: any) => {
    const name = typeof item === "string" ? item : item?.name;
    return name === permission && isPermissionEnabled(item);
  });
};

export const canManageAdmins = (): boolean =>
  isSuperAdmin() || hasPermission("admins");

export const canManageUsers = (): boolean =>
  isSuperAdmin() || hasPermission("users");

export const getFirstAccessibleRoute = (): string => {
  const routes = Object.entries(ROUTE_PERMISSIONS);
  const match = routes.find(([, permission]) => hasPermission(permission));
  return match?.[0] || "/profile";
};

export const buildPermissionState = (
  enabledKeys: AdminPermissionKey[] = [],
): AdminPermissionState[] =>
  ADMIN_PERMISSIONS.map((item) => ({
    name: item.key,
    isActive: item.key === "profile" || enabledKeys.includes(item.key),
  }));

export const buildAssignablePermissionState = (
  enabledKeys: AdminPermissionKey[] = [],
): AdminPermissionState[] =>
  ASSIGNABLE_PERMISSIONS.map((item) => ({
    name: item.key,
    isActive: enabledKeys.includes(item.key),
  }));

export const getEnabledPermissionKeys = (permissions: any[] = []): AdminPermissionKey[] =>
  permissions
    .filter((item) => isPermissionEnabled(item))
    .map((item) => (typeof item === "string" ? item : item.name))
    .filter((key): key is AdminPermissionKey =>
      ADMIN_PERMISSIONS.some((item) => item.key === key),
    );

export const canAccessRoute = (pathname: string): boolean => {
  if (pathname === "/profile" || pathname.startsWith("/profile/")) return true;

  if (pathname.startsWith("/users/")) return hasPermission("users");
  if (pathname.startsWith("/sale-users/")) return hasPermission("users");
  if (pathname.startsWith("/orders/")) return hasPermission("orders");
  if (pathname.startsWith("/products/edit/")) return hasPermission("products");
  if (pathname.startsWith("/admins/")) return hasPermission("admins");
  if (pathname.startsWith("/support/")) return hasPermission("support");

  const baseRoute = Object.keys(ROUTE_PERMISSIONS).find(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );

  if (!baseRoute) return true;
  return hasPermission(ROUTE_PERMISSIONS[baseRoute]);
};
